import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { LoadingButton } from "@mui/lab";
import { Box, Button } from "@mui/material";
import { Contact, useGetContactLazyQuery, User } from "@repo/server-api";
import { RuleFactoryType, TabKeys } from "@repo/server-api/types";
import { useNotification, RuleComponent } from "@repo/shared/components";
import { useTranslation } from "next-i18next";
import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { validErrorState } from "@repo/legacy/components/helper/Index";
import {
	useUpdateCompanyContact,
	useUpdatePersonContact,
} from "@repo/legacy/components/hooks/ContactMutationHooks";
import {
	useCreateUserLogin,
	useUpdateUserLogin,
} from "@repo/legacy/components/hooks/UserMutationHooks";
import ExternalWorkingConditions from "@repo/legacy/components/shared/additionalInformation/ExternalWorkingConditions";
import LoginSection from "@repo/legacy/components/shared/sections/LoginSection";
import WorkingConditionSection from "@repo/legacy/components/shared/sections/WorkingConditioins";
import usePersistentState from "@repo/legacy/store/usePersistentState";

// import { useContactForm } from "@repo/shared/components/shared/hooks/useContactForm";

import GeneralInfo from "../../components/shared/projects/GeneralInfoContact";
import Tabs from "../../components/shared/Tabs";

const ContactComponent = (props: { ruleFactory: RuleFactoryType }) => {
	const { t, i18n } = useTranslation();

	const navigate = useNavigate();

	const [contact, setContact] = usePersistentState("contact", null);

	const [tabIndex, setTabIndex] = useState<number>(0);

	const [user, setUser] = useState<User | null>(null);

	const [saveClicked, setSaveClicked] = useState<boolean>(false);

	const { showNotification } = useNotification();

	const [updateCompanyContact] = useUpdateCompanyContact();
	const [updatePersonContact] = useUpdatePersonContact();

	const [updateUserLogin] = useUpdateUserLogin();
	const [createUserLogin] = useCreateUserLogin();

	const { id } = useParams<{ id: string }>();

	const [errors, setErrors] = useState<any>({});

	const salaryInformationRef = useRef<{
		validate: () => Promise<Record<string, boolean | object | string>>;
	}>(null);
	const externalWorkingConditionsRef = useRef<{
		validate: () => Promise<Record<string, boolean | object | string>>;
	}>(null);
	const paymentInformationRef = useRef<{
		validate: () => Promise<Record<string, boolean | object | string>>;
	}>(null);
	const generalInfoRef = useRef<{
		validate: () => Promise<Record<string, boolean | object | string>>;
	}>(null);
	const loginRef = useRef<{
		validate: () => Promise<Record<string, boolean | object | string>>;
	}>(null);

	const [getContact] = useGetContactLazyQuery({
		fetchPolicy: "no-cache",
		variables: { id: id ?? "" },
		onCompleted(data) {
			if (data && data?.node) {
				setContact(data.node as Contact);
			} else {
				setContact(null);
			}
		},
	});

	const validate = async () => {
		const localErrors: Record<string, boolean | object | string> = {};

		switch (tabs[tabIndex].key) {
			case TabKeys.GENERAL_INFORMATION:
				localErrors.generalInfo = (await generalInfoRef.current?.validate()) || {};
				break;
			case TabKeys.SALARY_INFORMATION:
				localErrors.salaryInformation = (await salaryInformationRef.current?.validate()) || {};
				break;
			case TabKeys.EXTERNAL_WORKING_CONDITIONS:
				localErrors.externalWorkingConditions =
					(await externalWorkingConditionsRef.current?.validate()) || {};
				break;
			case TabKeys.PAYMENT_INFORMATION:
				localErrors.paymentInformation = (await paymentInformationRef.current?.validate()) || {};
				break;
			case TabKeys.LOGIN:
				localErrors.login = (await loginRef.current?.validate()) || {};
				break;
			default:
				break;
		}

		setErrors(localErrors);

		const isValid = validErrorState(localErrors);
		return isValid;
	};

	const handleSubmit = async () => {
		const isValid = await validate();
		if (isValid) {
			setSaveClicked(true);
			handleSave();
		}
	};

	const handleSubmitLogin = async () => {
		const isValid = await validate();
		if (isValid) {
			setSaveClicked(true);
			handleSaveLogin();
		}
	};

	const handleSaveLogin = async () => {
		if (user?.id) {
			updateUserLogin(user)
				.then((data: any) => {
					data.forEach((d: any) => {
						if (d.userChangeGroups) {
							if (d.userChangeGroups.errors) {
								d.userChangeGroups.errors.forEach((err: any) => {
									showNotification(err.message, "error");
								});
							}
							if (d.userChangeGroups.user) {
								showNotification(t("login.groupsChanged"), "success");
							}
						}
						if (d.userSetEnabled) {
							if (d.userSetEnabled.errors) {
								d.userSetEnabled.errors.forEach((err: any) => {
									showNotification(err.message, "error");
								});
							}
							if (d.userSetEnabled.user) {
								if (d.userSetEnabled.user.enabled) showNotification(t("login.enabled"), "success");
								else showNotification(t("login.disabled"), "success");
							}
						}
					});
					setSaveClicked(false);
				})
				.catch((error: any) => {
					showNotification(error.message, "error");
					setSaveClicked(false);
				});
		} else {
			createUserLogin(contact, user)
				.then((res: any) => {
					if (typeof res === "string") showNotification(t("login.userCreated"), "success");
					else showNotification(res.message, "error");
					setSaveClicked(false);
				})
				.catch((err) => {
					showNotification(err.message, "error");
					setSaveClicked(false);
				});
		}
	};

	const handleSave = async () => {
		if (contact?.isACompany) {
			updateCompanyContact(contact)
				.then((res) => {
					if (typeof res === "string") showNotification(t("company-updated"), "success");
					else showNotification(res.message, "error");
					setSaveClicked(false);
				})
				.catch((err) => {
					showNotification(err.message, "error");
					setSaveClicked(false);
				});
		} else {
			updatePersonContact(contact)
				.then((res) => {
					if (res.id) showNotification(t("person-updated"), "success");
					else showNotification(res.message, "error");
					setSaveClicked(false);
				})
				.catch((err) => {
					showNotification(err.message, "error");
					setSaveClicked(false);
				});
		}

		// Reload from backend
		setTimeout(() => getContact(), 500);
	};

	const handleCancel = () => {
		setContact(null);
		setSaveClicked(false);
		navigate(-1);
	};

	const allTabs = useMemo(
		() => [
			{
				label: t("general"),
				key: TabKeys.GENERAL_INFORMATION,
				content: (
					<GeneralInfo
						ref={generalInfoRef}
						errors={errors.generalInfo}
						setErrors={(value) =>
							setErrors({
								...errors,
								generalInfo: { ...errors.generalInfo, ...value },
							})
						}
						contact={contact}
						setContact={setContact}
						ruleFactory={props.ruleFactory}
					/>
				),
			},
			{
				label: t("tab.working-condition"),
				key: TabKeys.EXTERNAL_WORKING_CONDITIONS,
				content: (
					<ExternalWorkingConditions
						ref={externalWorkingConditionsRef}
						errors={errors.workingConditions}
						setErrors={(value) =>
							setErrors({
								...errors,
								externalWorkingConditions: {
									...errors.externalWorkingConditions,
									...value,
								},
							})
						}
						contact={contact}
						setContact={setContact}
					/>
				),
			},
			{
				label: t("tab.working-condition"),
				key: TabKeys.WORKING_CONDITIONS,
				content: (
					<WorkingConditionSection
						errors={errors.workingConditions}
						setErrors={(value) =>
							setErrors({
								...errors,
								workingConditions: { ...errors.workingConditions, ...value },
							})
						}
						contact={contact}
						setContact={setContact}
					/>
				),
			},
			{
				label: t("tab.login"),
				key: TabKeys.LOGIN,
				content: (
					<LoginSection
						ref={loginRef}
						user={user}
						setUser={setUser}
						contact={contact}
						errors={errors.login}
						setErrors={(value) => setErrors({ ...errors, login: { ...errors.login, ...value } })}
					/>
				),
			},
		],
		[contact, t, errors, setContact, getContact, user],
	);

	const [tabs, setTabs] = useState(allTabs);

	useEffect(() => {
		if (id !== contact?.id) getContact();
	}, [id, contact?.id, getContact]);

	const refreshTabs = useCallback(() => {
		const allowedTabs = props.ruleFactory.allowedTabsForContact(contact);
		const tabs = [...allTabs.filter((tab) => allowedTabs.includes(tab.key))];

		setTabs(tabs);
	}, [contact, allTabs]);

	useEffect(() => {
		refreshTabs();
	}, [errors, contact, t, i18n.language, refreshTabs]);

	return (
		<Box
			style={{
				minHeight: "90vh",
				width: "100%",
				display: "flex",
				flexDirection: "column",
			}}>
			<Box>
				<Button className="left" variant="outlined" onClick={handleCancel}>
					<ArrowBackIcon />
				</Button>
				<div className="left purpleHeader" style={{ marginLeft: 20 }}>
					{contact?.displayName}
					<span className="lightGreyFont" style={{ marginLeft: 10, fontSize: "12px" }}>
						({contact?.displayContactType.map((x) => t(x.toLowerCase())).join(", ")})
					</span>
				</div>
			</Box>
			<Box sx={{ flex: 1 }}>
				<Tabs
					tabs={tabs}
					tabIndex={tabIndex}
					setTabIndex={setTabIndex}
					rulefactory={props.ruleFactory}
				/>
			</Box>
			{tabs[tabIndex] && (
				<Box sx={{ display: "flex", justifyContent: "flex-end", py: 2, pr: "30px" }}>
					<RuleComponent rule={props.ruleFactory.generalSaveButton(tabs[tabIndex].key)}>
						<LoadingButton
							loading={saveClicked}
							className={saveClicked ? "" : "blueButton"}
							variant="contained"
							color="secondary"
							onClick={handleSubmit}>
							{t("save")}
						</LoadingButton>
					</RuleComponent>
					<RuleComponent rule={props.ruleFactory.loginSaveButton(tabs[tabIndex].key)}>
						<LoadingButton
							loading={saveClicked}
							className={saveClicked ? "" : "blueButton"}
							variant="contained"
							color="secondary"
							onClick={handleSubmitLogin}>
							{t("save")}
						</LoadingButton>
					</RuleComponent>
				</Box>
			)}
		</Box>
	);
};

export default ContactComponent;
